import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { beneficiariesAPI, membersAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { DobField } from './Profile';

const initialForm = {
  full_name: '',
  gender: '',
  relationship: '',
  phone: '',
  email: '',
  address: '',
  date_of_birth: '',
};

const DEFAULT_INSTALLMENT = 3000000;
const TOTAL_BENEFIT = DEFAULT_INSTALLMENT * 2;

const GENDER_OPTIONS = ['Male', 'Female'];

const RELATIONSHIP_BY_GENDER = {
  Male:   ['Spouse', 'Son', 'Father', 'Brother', 'Guardian', 'Other'],
  Female: ['Spouse', 'Daughter', 'Mother', 'Sister', 'Guardian', 'Other'],
  '':     ['Spouse', 'Son', 'Daughter', 'Father', 'Mother', 'Brother', 'Sister', 'Guardian', 'Other'],
};

const REL_COLOUR = {
  spouse:   { bg: 'bg-rose-500',   light: 'bg-rose-50',   text: 'text-rose-700'   },
  son:      { bg: 'bg-blue-500',   light: 'bg-blue-50',   text: 'text-blue-700'   },
  daughter: { bg: 'bg-pink-500',   light: 'bg-pink-50',   text: 'text-pink-700'   },
  father:   { bg: 'bg-purple-500', light: 'bg-purple-50', text: 'text-purple-700' },
  mother:   { bg: 'bg-violet-500', light: 'bg-violet-50', text: 'text-violet-700' },
  brother:  { bg: 'bg-amber-500',  light: 'bg-amber-50',  text: 'text-amber-700'  },
  sister:   { bg: 'bg-orange-500', light: 'bg-orange-50', text: 'text-orange-700' },
};
function relColour(rel) {
  return REL_COLOUR[(rel || '').toLowerCase()] ?? { bg: 'bg-slate-500', light: 'bg-slate-50', text: 'text-slate-700' };
}
function initials(name) {
  return (name || '??').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

export default function Beneficiaries() {
  const { user } = useAuthStore();
  const location = useLocation();
  const [nextOfKin, setNextOfKin]               = useState([]);
  const [installmentOneBeneficiary, setInstOne] = useState(null);
  const [installmentTwoBeneficiary, setInstTwo] = useState(null);
  const [member, setMember]                     = useState(null);
  const [loading, setLoading]                   = useState(true);
  const [saving, setSaving]                     = useState(false);
  const [selectingKey, setSelectingKey]         = useState('');
  const [modalOpen, setModalOpen]               = useState(false);
  const [editingKin, setEditingKin]             = useState(null);
  const [error, setError]                       = useState('');
  const [success, setSuccess]                   = useState('');
  const [formData, setFormData]                 = useState(initialForm);
  const [formError, setFormError]               = useState('');

  const selectedRelationship = (formData.relationship || '').toLowerCase();
  const requiresAdultDob     = selectedRelationship === 'son' || selectedRelationship === 'daughter';
  const instOneId            = installmentOneBeneficiary?.id ?? null;
  const instTwoId            = installmentTwoBeneficiary?.id ?? null;

  useEffect(() => {
    if (user?.role !== 'member') {
      setError('Only members can manage next of kin from this page.');
      setLoading(false);
      return;
    }
    membersAPI.me().then(p => {
      setMember(p);
      return refreshKin(p.id);
    }).catch(e => {
      setError(e.response?.data?.error || 'Failed to load next of kin.');
    }).finally(() => setLoading(false));
  }, [user]);

  const refreshKin = async (memberId) => {
    const r = await beneficiariesAPI.getByMember(memberId);
    setNextOfKin(r.beneficiaries || []);
    setInstOne(r.selected_installment_one_beneficiary || null);
    setInstTwo(r.selected_installment_two_beneficiary || null);
  };

  const openAdd = () => {
    setEditingKin(null);
    setFormData(initialForm);
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (kin) => {
    setEditingKin(kin);
    setFormData({
      full_name:     kin.full_name     || '',
      gender:        kin.gender        || '',
      relationship:  kin.relationship  || '',
      phone:         kin.phone         || '',
      email:         kin.email         || '',
      address:       kin.address       || '',
      date_of_birth: kin.date_of_birth || '',
    });
    setFormError('');
    setModalOpen(true);
  };

  const closeModal = () => { setModalOpen(false); setEditingKin(null); setFormData(initialForm); setFormError(''); };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(f => {
      const next = { ...f, [name]: value };
      if (name === 'gender') next.relationship = '';
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!member) { setFormError('Member profile not found.'); return; }
    setSaving(true); setFormError('');

    if (requiresAdultDob) {
      const dob   = new Date(formData.date_of_birth);
      const today = new Date();
      if (!formData.date_of_birth || isNaN(dob.getTime())) { setFormError('Valid date of birth is required.'); setSaving(false); return; }
      if (dob > today)                                       { setFormError('Date of birth cannot be in the future.'); setSaving(false); return; }
      const minAdult = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
      if (dob > minAdult)                                    { setFormError('Son/Daughter must be 18 years or older.'); setSaving(false); return; }
    }

    try {
      if (editingKin) {
        await beneficiariesAPI.update(editingKin.id, { ...formData });
        await refreshKin(member.id);
        setSuccess(`${formData.full_name} updated successfully.`);
      } else {
        await beneficiariesAPI.create({ member_id: member.id, ...formData, benefits: 0 });
        await refreshKin(member.id);
        setSuccess('Next of kin added. Now select who receives each benefit below.');
      }
      closeModal();
    } catch (err) {
      setFormError(err.response?.data?.error || (editingKin ? 'Failed to update.' : 'Failed to add.'));
    } finally { setSaving(false); }
  };

  const handleRemove = async (kin) => {
    if (!window.confirm(`Remove ${kin.full_name}?`)) return;
    setError(''); setSuccess('');
    try {
      await beneficiariesAPI.delete(kin.id);
      await refreshKin(member.id);
      setSuccess(`${kin.full_name} removed.`);
    } catch (err) { setError(err.response?.data?.error || 'Failed to remove.'); }
  };

  const handleSelect = async (kin, num) => {
    if (!member) return;
    const key = `${kin.id}-${num}`;
    setSelectingKey(key); setError(''); setSuccess('');
    try {
      await beneficiariesAPI.update(kin.id, num === 1
        ? { installment_one_selected: true, installment_one_amount: DEFAULT_INSTALLMENT }
        : { installment_two_selected: true, installment_two_amount: DEFAULT_INSTALLMENT });
      await refreshKin(member.id);
      setSuccess(`${kin.full_name} selected for ${num === 1 ? 'First' : 'Second'} Benefit.`);
    } catch (err) { setError(err.response?.data?.error || 'Failed to select.'); }
    finally { setSelectingKey(''); }
  };

  return (
    <div className="space-y-6">

      {location.state?.notice && !instOneId && (
        <div className="notice-blink rounded-2xl border-2 border-red-300 bg-red-50 px-4 py-3 text-sm font-bold text-red-600">
          {location.state.notice}
        </div>
      )}

      {/* ── Header ── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Next of Kin &amp; Beneficiaries</h1>
          <p className="mt-1 text-sm text-slate-500 max-w-lg">
            Add next of kin, then assign who receives the <strong>First Benefit</strong> (TZS {Number(TOTAL_BENEFIT).toLocaleString()}) and the <strong>Second Benefit</strong> (contingent — receives the payout if the First Beneficiary is unavailable).
          </p>
        </div>
        {user?.role === 'member' && (
          <button onClick={openAdd}
            className="shrink-0 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 text-sm font-semibold shadow-sm transition-colors">
            + Add Next of Kin
          </button>
        )}
      </div>

      {/* ── Alerts ── */}
      {error   && <div className="rounded-2xl border border-red-200   bg-red-50   px-4 py-3 text-sm text-red-700">{error}</div>}
      {success && <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{success}</div>}

      {loading && (
        <div className="flex items-center gap-3 text-slate-500 py-10 justify-center">
          <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
          </svg>
          Loading…
        </div>
      )}

      {!loading && (
        <>
          {/* ── Benefit Summary ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <BenefitSummaryCard label="First Benefit"  amount={`TZS ${Number(TOTAL_BENEFIT).toLocaleString()}`} beneficiary={installmentOneBeneficiary} colour="emerald" />
            <BenefitSummaryCard label="Second Benefit" beneficiary={installmentTwoBeneficiary} colour="blue" />
          </div>

          {/* ── Next of Kin Cards ── */}
          {nextOfKin.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {nextOfKin.map(kin => {
                const isOne  = instOneId === kin.id;
                const isTwo  = instTwoId === kin.id;
                const busy   = Boolean(selectingKey);
                const selOne = selectingKey === `${kin.id}-1`;
                const selTwo = selectingKey === `${kin.id}-2`;
                const col    = relColour(kin.relationship);

                return (
                  <div key={kin.id} className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col">
                    {/* Card header */}
                    <div className="px-5 py-4 flex items-center gap-3 border-b border-slate-100">
                      <div className={`h-11 w-11 shrink-0 rounded-full ${col.bg} flex items-center justify-center text-white font-bold text-sm`}>
                        {initials(kin.full_name)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-slate-800 text-sm truncate">{kin.full_name}</p>
                        <span className={`inline-block mt-0.5 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${col.light} ${col.text}`}>
                          {kin.relationship}
                        </span>
                      </div>
                      {(isOne || isTwo) && (
                        <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold ${isOne ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                          {isOne ? '1st' : '2nd'}
                        </span>
                      )}
                    </div>

                    {/* Contact info */}
                    <div className="px-5 py-4 space-y-2 flex-1">
                      <InfoRow label="Phone"  value={kin.phone || '—'} />
                      <InfoRow label="Email"  value={kin.email || '—'} />
                      {kin.date_of_birth && <InfoRow label="DOB"    value={kin.date_of_birth} />}
                      {kin.gender        && <InfoRow label="Gender" value={kin.gender} />}
                    </div>

                    {/* Benefit assignment */}
                    <div className="px-5 pb-3 grid grid-cols-2 gap-2">
                      <button disabled={isOne || busy} onClick={() => handleSelect(kin, 1)}
                        className={`rounded-xl py-2 text-xs font-semibold transition-colors ${isOne ? 'bg-emerald-100 text-emerald-700 cursor-default' : 'bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50'}`}>
                        {isOne ? '✓ 1st Benefit' : selOne ? 'Selecting…' : 'Set 1st Benefit'}
                      </button>
                      <button disabled={isTwo || busy} onClick={() => handleSelect(kin, 2)}
                        className={`rounded-xl py-2 text-xs font-semibold transition-colors ${isTwo ? 'bg-blue-100 text-blue-700 cursor-default' : 'bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50'}`}>
                        {isTwo ? '✓ 2nd Benefit' : selTwo ? 'Selecting…' : 'Set 2nd Benefit'}
                      </button>
                    </div>

                    {/* Edit / Remove */}
                    <div className="px-5 pb-4 grid grid-cols-2 gap-2">
                      <button disabled={busy} onClick={() => openEdit(kin)}
                        className="rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700 py-2 text-xs font-semibold transition-colors disabled:opacity-50">
                        Edit
                      </button>
                      <button disabled={busy} onClick={() => handleRemove(kin)}
                        className="rounded-xl border border-red-100 bg-red-50 hover:bg-red-100 text-red-600 py-2 text-xs font-semibold transition-colors disabled:opacity-50">
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-12 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-2xl">👤</div>
              <p className="font-semibold text-slate-700">No next of kin added yet</p>
              <p className="mt-1 text-sm text-slate-400">Click "+ Add Next of Kin" to get started.</p>
            </div>
          )}
        </>
      )}

      {/* ── Modal ── */}
      {modalOpen && (
        <KinFormModal
          editingKin={editingKin}
          formData={formData}
          formError={formError}
          saving={saving}
          requiresAdultDob={requiresAdultDob}
          onChange={handleChange}
          onSubmit={handleSubmit}
          onClose={closeModal}
        />
      )}
    </div>
  );
}

// ── Modal form ───────────────────────────────────────────────────────────────

function KinFormModal({ editingKin, formData, formError, saving, requiresAdultDob, onChange, onSubmit, onClose }) {
  const overlayRef = useRef(null);
  const col = editingKin ? relColour(editingKin.relationship) : { bg: 'bg-blue-600' };

  const handleBackdrop = (e) => { if (e.target === overlayRef.current) onClose(); };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      ref={overlayRef}
      onClick={handleBackdrop}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
    >
      <div className="w-full max-w-lg rounded-3xl bg-white shadow-2xl overflow-hidden animate-fade-in">

        {/* Modal header */}
        <div className={`${col.bg} px-6 py-5 flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            {editingKin && (
              <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-sm">
                {initials(editingKin.full_name)}
              </div>
            )}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-white/60">
                {editingKin ? 'Editing' : 'New Next of Kin'}
              </p>
              <p className="text-base font-bold text-white leading-tight">
                {editingKin ? editingKin.full_name : 'Add Next of Kin'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="rounded-full p-1.5 hover:bg-white/20 text-white transition-colors">
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
            </svg>
          </button>
        </div>

        {/* Modal body */}
        <form onSubmit={onSubmit} className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">

          {formError && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {formError}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Full Name"     name="full_name"     value={formData.full_name}     onChange={onChange} required />
            <SelectField label="Gender"       name="gender"       value={formData.gender}       onChange={onChange} options={GENDER_OPTIONS} />
            <SelectField label="Relationship" name="relationship" value={formData.relationship} onChange={onChange} options={RELATIONSHIP_BY_GENDER[formData.gender] ?? RELATIONSHIP_BY_GENDER['']} required />
            <Field label="Phone"         name="phone"         value={formData.phone}         onChange={onChange} />
            <Field label="Email"         name="email"         type="email" value={formData.email} onChange={onChange} />
            <DobField value={formData.date_of_birth} onChange={onChange} minAge={0} />
          </div>

          {requiresAdultDob && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
              Date of birth required for Son/Daughter — must be 18 years or older.
            </p>
          )}

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Address</label>
            <textarea name="address" value={formData.address} onChange={onChange} rows="2"
              className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none" />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button type="submit" disabled={saving}
              className="flex-1 rounded-2xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-2.5 text-sm font-semibold transition-colors">
              {saving ? 'Saving…' : editingKin ? 'Save Changes' : 'Add Next of Kin'}
            </button>
            <button type="button" onClick={onClose}
              className="rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 px-5 py-2.5 text-sm font-semibold transition-colors">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function BenefitSummaryCard({ label, amount, beneficiary, colour }) {
  const cfg = {
    emerald: { header: 'bg-emerald-600', dot: 'bg-emerald-500' },
    blue:    { header: 'bg-blue-600',    dot: 'bg-blue-500'    },
  }[colour];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className={`${cfg.header} px-5 py-3 flex items-center justify-between`}>
        <p className="text-sm font-bold text-white">{label}</p>
        {amount && <span className="text-xs font-semibold text-white/70">{amount}</span>}
      </div>
      <div className="px-5 py-4">
        {beneficiary ? (
          <div className="flex items-center gap-3">
            <span className={`h-2 w-2 rounded-full shrink-0 ${cfg.dot}`} />
            <div>
              <p className="font-bold text-slate-800 text-sm">{beneficiary.full_name}</p>
              <p className="text-xs text-slate-500">{beneficiary.relationship}</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-400 italic">Not yet assigned</p>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</span>
      <span className="text-xs font-medium text-slate-700 text-right max-w-[60%] truncate">{value}</span>
    </div>
  );
}

function Field({ label, name, type = 'text', value, onChange, required = false }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input type={type} name={name} value={value} onChange={onChange} required={required}
        className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
    </div>
  );
}

function SelectField({ label, name, value, onChange, options, required = false }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <select name={name} value={value} onChange={onChange} required={required}
        className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white">
        <option value="">Select {label.toLowerCase()}</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
